import React, {
  CSSProperties,
  DragEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactQuill, { Quill, ReactQuillProps } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EditorToolbar, {
  EditorToolbarProps,
  redoChange,
  undoChange,
} from './CronoEditorToolbar';
import { EditorWrapper } from './styles';
import { FeConstants } from 'constants/FeConstants';
import useUploadFiles from 'hooks/services/templates/useUploadFiles';
import { useConditionalSnackBar } from 'context/snackbar';
import { getError } from 'crono-fe-common/utils';
import { useAnalytics } from 'context/analytics';
import { useImageFilePicker } from 'hooks/useImageFilePicker';
import { FileContent } from 'use-file-picker';
import { hasBase64ImageTag } from 'utils/validationUtils';
import CreateSnippetButton from './createSnippetButton';

class PreserveWhiteSpace {
  constructor(quill: {
    container: {
      style: CSSProperties;
    };
  }) {
    quill.container.style.whiteSpace = 'pre-line';
  }
}
Quill.register('modules/preserveWhiteSpace', PreserveWhiteSpace);

const Link = Quill.import('formats/link');
class CronoLink extends Link {
  // Add https:// to those links that do not have it.
  static sanitize(url: string) {
    const sanitized = super.sanitize(url);
    if (
      sanitized === Link.SANITIZED_URL ||
      /^(https?:|mailto:|tel:|sms:|\/\/|#)/i.test(sanitized)
    ) {
      return sanitized;
    }
    return `https://${sanitized}`;
  }
}
Quill.register(CronoLink, true);

export interface ICronoEditorProps
  extends Omit<ReactQuillProps, 'onBlur'>, EditorToolbarProps {
  editorRef: React.MutableRefObject<ReactQuill | null>;
  onFocus?: () => void;
  imageSupport?: boolean;
  templateComponent?: JSX.Element | null;
  closeTemplateTooltip?: () => void;
  showTemplateTab?: boolean;
  onAttachmentDrop?: (files: FileContent[]) => void;
  //If this is true we do not substitute the snippet with the variable name after creation. This has to be true when we are in context in which the variables are already substituted
  //while in template creation this can remain false
  disableReplaceSnippetAfterCreation?: boolean;

  whiteBackground?: boolean;

  sendButton?: JSX.Element;

  hideToolbar?: boolean;

  onBlur?: (value: string) => void;
}

const CronoEditor = ({
  editorRef,
  children,
  toolbarId = 'toolbar',
  imageSupport = false,
  templateComponent,
  closeTemplateTooltip,
  showTemplateTab,
  customButtons,
  onAttachmentDrop,
  hideStylingButtons,
  onChangeSelection,
  disableReplaceSnippetAfterCreation = false,
  whiteBackground = false,
  sendButton,
  hideToolbar = false,
  onBlur,
  ...props
}: ICronoEditorProps) => {
  const { value: propValue, onChange: propOnChange, ...restQuillProps } = props;

  const editorEmittedRef = useRef<string | null>(null);
  const lastPropValueRef = useRef<typeof propValue>(propValue);

  let effectiveValue = propValue;
  if (typeof propValue === 'string') {
    if (propValue !== lastPropValueRef.current) {
      lastPropValueRef.current = propValue;
      editorEmittedRef.current = null;
    } else if (editorEmittedRef.current !== null) {
      effectiveValue = editorEmittedRef.current;
    }
  }

  const modules = {
    toolbar: hideToolbar
      ? false
      : {
          container: `#${toolbarId}`,
          handlers: {
            undo: undoChange,
            redo: redoChange,
          },
        },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize'],
    },
    preserveWhiteSpace: true,
    clipboard: {
      matchVisual: false,
    },
  };

  const [imageSupportIsDisabledError, setImageSupportIsDisabledError] =
    useState<boolean>(false);
  const [imageSizeLimitReachedError, setImageSizeLimitReachedError] =
    useState<boolean>(false);
  const [base64ImageError, setBase64ImageError] = useState<boolean>(false);

  const {
    mutate: uploadFiles,
    data: uploadFilesResponse,
    error: uploadFilesError,
  } = useUploadFiles();

  useConditionalSnackBar([
    {
      condition: !!uploadFilesError,
      message: getError(uploadFilesError) ?? 'Image upload has failed',
      severity: 'error',
    },
    {
      condition: imageSupportIsDisabledError,
      message: 'You can upload image only for an email',
      severity: 'error',
    },
    {
      condition: imageSizeLimitReachedError,
      message: 'You can only upload images up to 5MB',
      severity: 'error',
    },
    {
      condition: base64ImageError,
      message:
        'We cannot upload your image. Try to save it locally and upload it from your computer',
      severity: 'error',
    },
  ]);

  const insertImagePlaceholders = (count: number) => {
    if (editorRef.current) {
      const editor = editorRef.current.getEditor();
      const range = editor.getSelection(true);
      const index = range.index + range.length;

      for (let i = 0; i < count; i++) {
        editor.insertEmbed(index + i, 'placeholder', {});
      }
    }
  };

  const deleteImagePlaceholders = () => {
    if (editorRef.current) {
      const editor = editorRef.current.getEditor();
      const contents = editor.getContents();
      let index = 0;
      const placeholders: number[] = [];

      contents.ops?.forEach((op) => {
        if (
          op.insert &&
          typeof op.insert === 'object' &&
          op.insert.placeholder
        ) {
          placeholders.push(index);
        }
        index += op.insert
          ? typeof op.insert === 'object'
            ? 1
            : op.insert.length
          : 0;
      });

      placeholders.reverse().forEach((pos) => {
        editor.deleteText(pos, 1);
      });
    }
  };

  const analytics = useAnalytics();

  const uploadImagesIfSupported = (files: File[]) => {
    if (files.length > 0) {
      if (imageSupport) {
        const maxFileSizeInBytes = FeConstants.uploadImageLimitInMb * 1_000_000;
        const oversizedFiles = files.filter((file) =>
          //We had error of file being null in the past, that's the reason of the check
          file ? file.size > maxFileSizeInBytes : false,
        );
        if (oversizedFiles.length > 0) {
          setImageSizeLimitReachedError(true);
          setTimeout(() => setImageSizeLimitReachedError(false), 3000);
          return;
        }

        if (analytics) {
          analytics.track('image-attached', {});
        }
        insertImagePlaceholders(files.length);
        const images: {
          name: string;
          content: string;
        }[] = [];
        let filesRead = 0;

        files.forEach((file) => {
          const reader = new FileReader();
          reader.onload = () => {
            images.push({
              name: file.name,
              content: reader.result as string,
            });
            filesRead++;

            // Check if all files have been read
            if (filesRead === files.length && editorRef.current) {
              uploadFiles({
                files: images,
              });
            }
          };
          reader.readAsDataURL(file);
        });
      } else {
        // we handle them as attachments in some cases inside TemplateManagement
        if (onAttachmentDrop) {
          handleAttachments(files);
        } else {
          setImageSupportIsDisabledError(true);
          setTimeout(() => setImageSupportIsDisabledError(false), 3000);
        }
      }
    }
  };

  const handleAttachments = (files: File[]) => {
    if (files.length > 0 && onAttachmentDrop) {
      const fileContents: FileContent[] = [];
      let filesRead = 0;

      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          // it was not working with ...file

          fileContents.push({
            name: file.name,
            size: file.size,
            type: file.type,
            slice: file.slice,
            text: file.text,
            stream: file.stream,
            arrayBuffer: file.arrayBuffer,
            lastModified: file.lastModified,
            bytes: file.bytes,
            content: reader.result as string,
          });
          filesRead++;

          // Check if all files have been read
          if (filesRead === files.length && editorRef.current) {
            onAttachmentDrop(fileContents);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const { dataTransfer } = event;

    if (dataTransfer && dataTransfer.files && dataTransfer.files.length > 0) {
      const images = Array.from(dataTransfer.files).filter((file) =>
        file.type.startsWith('image/'),
      ) as File[];

      uploadImagesIfSupported(images);

      const otherFiles = Array.from(dataTransfer.files).filter(
        (file) => !file.type.startsWith('image/'),
      ) as File[];

      handleAttachments(otherFiles);
    }
  };

  useEffect(() => {
    if (!editorRef.current) return;
    const editor = editorRef.current.getEditor();

    const handlePaste = (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      const htmlData = clipboardData.getData('text/html');
      if (hasBase64ImageTag(htmlData)) {
        setBase64ImageError(true);
        setTimeout(() => setBase64ImageError(false), 3000);
        event.preventDefault();
        return;
      }

      const items = clipboardData.items;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) files.push(file);
        }
      }

      // We do it here not to break text pasting
      if (files.length > 0) {
        event.preventDefault();
        event.stopPropagation();
      }
      uploadImagesIfSupported(files);
    };

    editor.root.addEventListener('paste', handlePaste);

    return () => {
      editor.root.removeEventListener('paste', handlePaste);
    };
  }, [editorRef.current]);

  useEffect(() => {
    const responseData = uploadFilesResponse?.data?.data;
    if (responseData?.length && responseData.length > 0) {
      if (editorRef.current) {
        const editor = editorRef.current.getEditor();
        const range = editor.getSelection(true);
        const index = range.index + range.length;

        deleteImagePlaceholders();
        responseData.forEach((imageName: string) => {
          const urlRepresentation = new URLSearchParams({ imageName });
          const encodedImageName = urlRepresentation.toString().split('=')[1];
          const imageUrl = `${process.env.VITE_APP_S3_FILES_URL}/${encodedImageName}`;
          editor.insertEmbed(index, 'image', imageUrl, 'user');

          setTimeout(() => {
            const insertedImage = editor.root.querySelector(
              `img[src="${imageUrl}"]`,
            );
            if (insertedImage) {
              insertedImage.setAttribute('width', '620px');
            }
          }, 0);
        });
      }
    }
  }, [uploadFilesResponse]);

  useEffect(() => {
    deleteImagePlaceholders();
  }, [uploadFilesError]);

  // this removes the image resize overlay (border) when clicking outside the image or scrolling editor content
  useEffect(() => {
    const quillEditorContext = editorRef.current;
    if (!quillEditorContext) return;
    const quill = quillEditorContext.getEditor();
    const imageResize = quill.getModule('imageResize');
    const quillRoot = quill.root;
    const hideOverlayOnClick = async (event: PointerEvent) => {
      const target = event.target as HTMLElement;
      if (!quillRoot.contains(target)) {
        imageResize.hide();
      }
    };
    const hideOverlay = async () => {
      imageResize.hide();
    };

    const wrapper = document.getElementById('editor-wrapper');
    const editor = document.getElementsByClassName('ql-editor')[0];

    wrapper?.addEventListener('click', hideOverlayOnClick);
    editor?.addEventListener('scroll', hideOverlay);

    return () => {
      wrapper?.removeEventListener('click', hideOverlayOnClick);
      editor?.removeEventListener('scroll', hideOverlay);
    };
  }, [editorRef.current]);

  const [openFileSelector, { filesContent, errorMessage }] =
    useImageFilePicker();
  useEffect(() => {
    if (filesContent.length > 0) {
      insertImagePlaceholders(filesContent.length);
      uploadFiles({ files: filesContent });
    }
  }, [filesContent]);

  const quillFormats = useMemo(() => {
    if (imageSupport) {
      return [
        ...FeConstants.editorToolbarFormats,
        ...FeConstants.editorToolbarAdvanceFormattingFormats,
        'image',
      ];
    } else if (!hideStylingButtons) {
      return [
        ...FeConstants.editorToolbarFormats,
        ...FeConstants.editorToolbarAdvanceFormattingFormats,
      ];
    } else {
      return FeConstants.editorToolbarFormats;
    }
  }, [imageSupport, hideStylingButtons]);

  const [positionCreateSnippet, setPositionCreateSnippet] = useState<{
    top: number;
    left: number;
    right: number;
    bottom: number;
    selection: ReactQuill.Range;
  } | null>(null);

  const [lastPosition, setLastPosition] = useState<ReactQuill.Range>(null);

  const handleSelectionForSnippet = (
    selection: ReactQuill.Range,
    source: unknown,
    editor: ReactQuill.UnprivilegedEditor,
  ) => {
    if (selection && selection.length > 0) {
      //Get only the last character of the selection
      const boundsSelection = editor.getBounds(
        selection.index + selection.length - 1,
      );
      setPositionCreateSnippet({
        top: boundsSelection.top,
        left: boundsSelection.left,
        right: boundsSelection.right,
        bottom: boundsSelection.bottom,
        selection: selection,
      });
      setLastPosition(selection);
    } else {
      setTimeout(() => {
        setPositionCreateSnippet(null);
      }, 100);
    }
  };

  const getCurrentlySelectedText = () => {
    if (!lastPosition) return;
    const sel = editorRef.current
      ?.getEditor()
      .getText(lastPosition.index, lastPosition.length);
    return sel;
  };
  //To give a boundary to the button if too big selection is made
  const editorHeight = useMemo(() => {
    //We get the value only if the editor is mounted correctly
    if (!editorRef.current?.editingArea) return 0;
    try {
      const a = editorRef.current?.getEditingArea();
      return a?.clientHeight ?? 0;
    } catch (e) {
      return 0;
    }
  }, [editorRef.current]);

  const editorWidth = useMemo(() => {
    //We get the value only if the editor is mounted correctly
    if (!editorRef.current?.editingArea) return 0;
    try {
      const a = editorRef.current?.getEditingArea();
      return a?.clientWidth ?? 0;
    } catch (e) {
      return 0;
    }
  }, [editorRef.current]);

  const handleReplaceSnippet = (templateVariableName: string) => {
    if (!disableReplaceSnippetAfterCreation) {
      const editor = editorRef.current?.getEditor();
      if (!editor || !lastPosition) return;
      if (lastPosition.length > 0) {
        editor.deleteText(lastPosition.index, lastPosition.length, 'user');
      }
      editor.insertText(lastPosition.index, templateVariableName, 'user');
    }
  };

  const toolbarRef = useRef<HTMLDivElement>(null);

  return (
    <EditorWrapper id={'editor-wrapper'} whiteBackground={whiteBackground}>
      {!props.readOnly && (
        <CreateSnippetButton
          positionCreateSnippet={positionCreateSnippet}
          editorHeight={editorHeight}
          editorWidth={editorWidth}
          handleReplaceSnippet={handleReplaceSnippet}
          getInitialContent={getCurrentlySelectedText}
          bottomOffset={toolbarRef.current?.clientHeight ?? 0}
        />
      )}
      {!hideToolbar && (
        <EditorToolbar
          {...props}
          toolbarRef={toolbarRef}
          toolbarId={toolbarId}
          editorRef={editorRef}
          closeTemplateTooltip={closeTemplateTooltip}
          showTemplateTab={showTemplateTab}
          templateComponent={templateComponent}
          hideStylingButtons={hideStylingButtons}
          customButtons={
            imageSupport && customButtons
              ? [
                  ...customButtons,
                  {
                    type: 'image-upload',
                    onClick: openFileSelector,
                  },
                ]
              : customButtons
          }
          sendButton={sendButton}
          whiteBackground={whiteBackground}
        />
      )}
      <div
        data-text-editor="editor-wrapper"
        className="quill-editor"
        style={{
          position: 'relative',
        }}
        onDrop={handleDrop}
      >
        <ReactQuill
          {...restQuillProps}
          value={effectiveValue}
          onChange={(text, delta, source, editor) => {
            editorEmittedRef.current = text;
            propOnChange?.(text, delta, source, editor);
          }}
          onBlur={(previousSelection, source) => {
            if (source !== 'user') return;
            onBlur?.(editorRef.current?.editor?.root.innerHTML ?? '');
          }}
          onChangeSelection={(selection, source, editor) => {
            if (onChangeSelection) {
              onChangeSelection(selection, source, editor);
            }
            handleSelectionForSnippet(selection, source, editor);
          }}
          ref={(editor) => {
            if (editor) {
              editorRef.current = editor;
            }
          }}
          id="editor"
          theme={props.readOnly ? 'bubble' : 'snow'}
          modules={modules}
          formats={quillFormats}
          bounds={`[data-text-editor="editor-wrapper"]`}
        />
      </div>
      {children}
    </EditorWrapper>
  );
};

export default CronoEditor;
